import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Slider, Input } from 'antd';
import SpinInputNumber from './SpinInputNumber';
import { DeleteOutlined, MenuFoldOutlined, FileAddOutlined, EyeOutlined, EyeInvisibleOutlined, MenuOutlined, DownOutlined, RightOutlined, RedoOutlined, WarningOutlined, VerticalAlignBottomOutlined, LockOutlined, UnlockOutlined, ClearOutlined } from '@ant-design/icons';
import { useAppStore, Layer, KeyframeProp } from '../store/useAppStore';
import { viewportActions } from '../viewportActions';
import { maybeAutoKey } from '../utils/autoKeyUtils';
import { getLayerDepth, isEffectivelyLocked } from '../utils/viewportMathUtils';

/** Returns layers sorted so every child appears below its parent (tree-walk order). */
function buildDisplayOrder(layers: Layer[]): Layer[] {
  const result: Layer[] = [];
  const visited = new Set<string>();
  const append = (layer: Layer) => {
    if (visited.has(layer.id)) return;
    visited.add(layer.id);
    result.push(layer);
    layers.filter((l) => l.parentId === layer.id).forEach(append);
  };
  layers
    .filter((l) => !l.parentId || !layers.some((p) => p.id === l.parentId))
    .forEach(append);
  return result;
}

let blankLayerThumbnailDataUrl: string | null = null;

const getBlankLayerThumbnailDataUrl = () => {
  if (blankLayerThumbnailDataUrl) return blankLayerThumbnailDataUrl;
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 64;
  blankLayerThumbnailDataUrl = canvas.toDataURL('image/webp', 0.7);
  return blankLayerThumbnailDataUrl;
};

// ── LayerToolButton — icon-only button, white at rest, colored on hover ─────
const LayerToolButton: React.FC<{
  icon: React.ReactNode;
  title: string;
  hoverColor: string;
  disabled?: boolean;
  onClick: () => void;
}> = ({ icon, title, hoverColor, disabled, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const active = hovered && !disabled;
  return (
    <Button
      icon={icon}
      size="small"
      title={title}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        color: active ? hoverColor : 'rgba(255,255,255,0.75)',
        borderColor: active ? hoverColor : 'rgba(255,255,255,0.2)',
        transition: 'color 0.15s, border-color 0.15s',
      }}
    />
  );
};

// ── DropIndicator — blue line shown at insertion point during drag ───────────
const DropIndicator: React.FC<{ active: boolean }> = ({ active }) => (
  <div
    style={{
      height: active ? 2 : 0,
      margin: active ? '-1px 0' : 0,
      background: active ? '#1890ff' : 'transparent',
      borderRadius: 1,
      flexShrink: 0,
      pointerEvents: 'none',
    }}
  />
);

// ── LayerRow ─────────────────────────────────────────────────────────────────
interface LayerRowProps {
  layer: Layer;
  isSelected: boolean;
  isExpanded: boolean;
  isEditing: boolean;
  nameDraft: string;
  thumbnail: string | undefined;
  shiftHeld: React.MutableRefObject<boolean>;
  /** Visual nesting depth (0 = root layer). */
  indentLevel: number;
  /** True when this row is the active drag-to-parent target. */
  isParentDropTarget?: boolean;
  /** True if any ancestor is locked (but this layer itself is not). */
  isAncestorLocked?: boolean;
  /** Called when the user clicks the Unparent button; undefined for root layers. */
  onUnparent?: () => void;
  selectLayer: (id: string | null) => void;
  updateLayer: (id: string, patch: Partial<Omit<Layer, 'id'>>) => void;
  onToggleExpanded: (id: string) => void;
  onStartEdit: (id: string, name: string) => void;
  onNameDraftChange: (name: string) => void;
  onNameCommit: (id: string, name: string) => void;
  onDragEnd: () => void;
  callMaybeAutoKey: (layerId: string, prop: KeyframeProp, value: boolean | number, forceKey?: boolean) => void;
}

const LayerRow = React.memo<LayerRowProps>(function LayerRow({
  layer, isSelected, isExpanded, isEditing, nameDraft, thumbnail,
  shiftHeld, indentLevel, isParentDropTarget, isAncestorLocked, onUnparent,
  selectLayer, updateLayer,
  onToggleExpanded, onStartEdit, onNameDraftChange, onNameCommit,
  onDragEnd, callMaybeAutoKey,
}) {
  const isOwnLocked  = !!layer.locked;
  const isLocked     = isOwnLocked || !!isAncestorLocked;
  const opacityPct = Math.round(layer.opacity * 100);
  // Push exactly one snapshot per opacity-slider drag session.
  const opacitySnapRef = useRef(false);
  return (
    // Indent wrapper + left-border accent for child layers
    <div style={{
      marginLeft: indentLevel * 16,
      borderLeft: indentLevel > 0 ? '2px solid rgba(245,166,35,0.35)' : undefined,
      paddingLeft: indentLevel > 0 ? 4 : 0,
    }}>
    <div
      style={{
        borderRadius: 6,
        border: isParentDropTarget
          ? '1px solid rgba(24,144,255,0.8)'
          : isSelected
            ? '1px solid rgba(24, 144, 255, 0.5)'
            : '1px solid rgba(255,255,255,0.08)',
        background: isParentDropTarget
          ? 'rgba(24,144,255,0.14)'
          : isSelected ? 'rgba(24, 144, 255, 0.08)' : 'rgba(255,255,255,0.03)',
        overflow: 'hidden',
      }}
    >
      {/* ── Header row ── */}
      <div
        style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px 6px 8px', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => selectLayer(layer.id)}
      >
        {/* Invisible drag overlay — full header height, from left edge to just before eye icon */}
        <div
          style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 34, cursor: isAncestorLocked ? 'not-allowed' : 'grab', zIndex: 1 }}
          draggable={!isAncestorLocked}
          onDragStart={(evt) => {
            if (isAncestorLocked) { evt.preventDefault(); return; }
            evt.dataTransfer.setData('text/plain', layer.id);
            evt.dataTransfer.effectAllowed = 'move';
          }}
          onDragEnd={onDragEnd}
          onMouseDown={(evt) => evt.stopPropagation()}
        />
        <MenuOutlined
          style={{ cursor: 'grab', opacity: 0.45, flexShrink: 0 }}
        />

        <Button
          type="text"
          size="small"
          disabled={isLocked}
          title={layer.visible ? 'Hide layer' : 'Show layer'}
          icon={layer.visible
            ? <EyeOutlined style={{ opacity: 0.4 }} />
            : <EyeInvisibleOutlined style={{ opacity: 0.3 }} />}
          style={{ position: 'relative', zIndex: 2 }}
          onClick={(e) => { e.stopPropagation(); viewportActions.pushSnapshot.current(); updateLayer(layer.id, { visible: !layer.visible }); }}
        />
        {/* Lock / Unlock — yellow if own-locked, faint-amber if ancestor-locked */}
        <Button
          type="text" size="small"
          icon={isOwnLocked
            ? <LockOutlined style={{ color: '#f5a623' }} />
            : isAncestorLocked
              ? <LockOutlined style={{ color: 'rgba(245,166,35,0.4)' }} />
              : <UnlockOutlined style={{ opacity: 0.4 }} />}
          title={isOwnLocked ? 'Unlock layer' : isAncestorLocked ? 'Locked via parent' : 'Lock layer'}
          style={{ position: 'relative', zIndex: 2 }}
          onClick={(e) => {
            e.stopPropagation();
            if (!isAncestorLocked) updateLayer(layer.id, { locked: !isOwnLocked });
          }}
        />
        {/* Unparent button — only shown for child layers */}
        {onUnparent && (
          <Button
            type="text" size="small"
            icon={<MenuFoldOutlined />}
            title="Remove parent (unparent layer)"
            style={{ position: 'relative', zIndex: 2, opacity: 0.5, padding: '0 3px' }}
            onClick={(e) => { e.stopPropagation(); onUnparent(); }}
          />
        )}

        {isEditing ? (
          <Input
            value={nameDraft}
            size="small"
            onChange={(e) => onNameDraftChange(e.target.value)}
            onBlur={() => onNameCommit(layer.id, nameDraft)}
            onPressEnter={() => onNameCommit(layer.id, nameDraft)}
            style={{ flex: 1 }}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            style={{ flex: 1, fontSize: 13, fontWeight: isSelected ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: isLocked ? undefined : 'text' }}
            onDoubleClick={(e) => { if (!isLocked) { e.stopPropagation(); onStartEdit(layer.id, layer.name); } }}
          >
            {layer.name}
          </span>
        )}

        {/* Dup-mode dot indicator */}
        {(() => {
          const dotCount = layer.lastDupMode === 'diptych' ? 2 : layer.lastDupMode === 'triptych' ? 3 : layer.lastDupMode === 'quadriptych' ? 4 : layer.lastDupMode === 'hero' ? 1 : 0;
          if (!dotCount) return null;
          return (
            <div
              title={layer.lastDupMode}
              style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2, flexShrink: 0, width: 6 }}
            >
              {Array.from({ length: dotCount }).map((_, i) => (
                <div key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.45)' }} />
              ))}
            </div>
          );
        })()}
        {thumbnail ? (
          <img
            src={thumbnail}
            alt="layer preview"
            style={{ width: 48, height: 24, objectFit: 'cover', borderRadius: 3, border: '1px solid rgba(0,0,0,0.5)', flexShrink: 0, imageRendering: 'pixelated' }}
          />
        ) : (
          <div style={{ width: 48, height: 24, borderRadius: 3, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', flexShrink: 0 }} />
        )}

        {/* Expand/collapse toggle */}
        <div
          title={isExpanded ? 'Collapse layer' : 'Expand layer'}
          style={{ flexShrink: 0, opacity: 0.5, fontSize: 10, padding: '0 2px' }}
          onClick={(e) => { e.stopPropagation(); onToggleExpanded(layer.id); }}
        >
          {isExpanded ? <DownOutlined /> : <RightOutlined />}
        </div>
      </div>

      {/* ── Collapsible details: opacity + rotation ── */}
      {isExpanded && (
        <div
          style={{ padding: '4px 12px 10px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', flexDirection: 'column', gap: 10, boxShadow: 'inset 0 0 5px rgba(0, 0, 0, 0.25)', opacity: isLocked ? 0.45 : 1, pointerEvents: isLocked ? 'none' : undefined }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Opacity */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, opacity: 0.55, flexShrink: 0, width: 46 }}>Opacity</span>
            <Slider
              min={0} max={100} step={1}
              value={opacityPct}
              onChange={(value) => {
                if (!opacitySnapRef.current) { opacitySnapRef.current = true; viewportActions.pushSnapshot.current(); }
                updateLayer(layer.id, { opacity: (value as number) / 100 });
                callMaybeAutoKey(layer.id, 'opacity', (value as number) / 100);
              }}
              onChangeComplete={() => { opacitySnapRef.current = false; }}
              tooltip={{ formatter: (v) => `${v}%` }}
              style={{ flex: 1, margin: 0 }}
            />
            <SpinInputNumber
              min={0} max={100} step={1}
              value={opacityPct}
              onChange={(value) => { const op = Number(value ?? 100) / 100; viewportActions.pushSnapshot.current(); updateLayer(layer.id, { opacity: op }); callMaybeAutoKey(layer.id, 'opacity', op); }}
              formatter={(v) => `${v}%`}
              parser={(v) => Number((v ?? '').replace('%', '')) as any}
              size="small"
              style={{ width: 62 }}
            />
          </div>

          {/* Rotation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, opacity: 0.55, flexShrink: 0, width: 46 }}>Rotate (°)</span>
            {(['x', 'y', 'z'] as const).map((axis) => (
              <div key={axis} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 10, opacity: 0.5, textTransform: 'uppercase', flexShrink: 0 }}>{axis}</span>
                <SpinInputNumber
                  value={layer.rotate[axis]}
                  step={1}
                  min={-360}
                  max={360}
                  onChange={(value) => {
                    const v = Number(value ?? 0);
                    viewportActions.pushSnapshot.current();
                    updateLayer(layer.id, { rotate: { ...layer.rotate, [axis]: v } });
                    callMaybeAutoKey(layer.id, `rotate${axis.toUpperCase()}` as KeyframeProp, v, shiftHeld.current);
                  }}
                  size="small"
                  style={{ width: '100%' }}
                />
              </div>
            ))}
            <Button
              size="small"
              type="text"
              title="Reset rotation"
              onClick={() => { viewportActions.pushSnapshot.current(); updateLayer(layer.id, { rotate: { x: 0, y: 0, z: 0 } }); }}
              icon={<RedoOutlined />}
              style={{ flexShrink: 0, opacity: 0.5, padding: '0 4px' }}
            />
          </div>

          {/* Translation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, opacity: 0.55, flexShrink: 0, width: 46 }}>Translate</span>
            {layer.translate.y !== 0 && (layer.rotate.x !== 0 || layer.rotate.y !== 0 || layer.rotate.z !== 0) && (
              <WarningOutlined
                title="Vertical translate (Y) is a UV shift; it does not compose perfectly with rotation. Use rotation alone for full 3-D transforms."
                style={{ color: '#f59e0b', fontSize: 11, flexShrink: 0, cursor: 'help' }}
              />
            )}
            {(['x', 'y'] as const).map((axis) => (
              <div key={axis} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ fontSize: 10, opacity: 0.5, textTransform: 'uppercase', flexShrink: 0 }}>{axis}</span>
                <SpinInputNumber
                  value={layer.translate[axis]}
                  step={0.01}
                  min={axis === 'y' ? -1 : undefined}
                  max={axis === 'y' ? 1 : undefined}
                  onChange={(value) => {
                    const v = Number(value ?? 0);
                    viewportActions.pushSnapshot.current();
                    updateLayer(layer.id, { translate: { ...layer.translate, [axis]: v } });
                    callMaybeAutoKey(layer.id, axis === 'x' ? 'translateX' : 'translateY', v);
                  }}
                  size="small"
                  style={{ width: '100%' }}
                />
              </div>
            ))}
            <Button
              size="small"
              type="text"
              title="Reset translation"
              onClick={() => { viewportActions.pushSnapshot.current(); updateLayer(layer.id, { translate: { x: 0, y: 0 } }); }}
              icon={<RedoOutlined />}
              style={{ flexShrink: 0, opacity: 0.5, padding: '0 4px' }}
            />
          </div>
        </div>
      )}
    </div>
    </div>
  );
});

const LayersPanel: React.FC = () => {
  const layers = useAppStore((state) => state.layers);
  const selectedLayerId = useAppStore((state) => state.selectedLayerId);
  const selectLayer = useAppStore((state) => state.selectLayer);
  const addLayer = useAppStore((state) => state.addLayer);
  const removeLayer = useAppStore((state) => state.removeLayer);
  const updateLayer = useAppStore((state) => state.updateLayer);
  const moveLayer = useAppStore((state) => state.moveLayer);
  const setLayersOrder = useAppStore((state) => state.setLayersOrder);
  const layerThumbnails = useAppStore((state) => state.layerThumbnails);
  const requestMergeLayerDown = useAppStore((state) => state.requestMergeLayerDown);


  // Track Shift key state so rotation onChange handlers can force-key even in mode 1.
  const shiftHeld = useRef(false);
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => { if (e.key === 'Shift') shiftHeld.current = true; };
    const onUp   = (e: KeyboardEvent) => { if (e.key === 'Shift') shiftHeld.current = false; };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup',   onUp);
    return () => { window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp); };
  }, []);

  // F2 → start renaming the selected layer (mirrors double-clicking the pencil icon).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'F2') return;
      const target = e.target as HTMLElement;
      // Ignore if focus is inside an input/textarea to avoid clobbering text editing.
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      e.preventDefault();
      if (!selectedLayerId) return;
      const layer = layers.find((l) => l.id === selectedLayerId);
      if (!layer) return;
      setEditingNameId(selectedLayerId);
      setNameDraft(layer.name);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedLayerId, layers]);

  // Auto-key helper. forceKey=true bypasses the mode-1 "must have existing keyframes" check.
  const callMaybeAutoKey = useCallback(
    (layerId: string, prop: KeyframeProp, value: boolean | number, forceKey = false) => {
      const { autoKey, currentFrame, layerKeyframes, setKeyframe } = useAppStore.getState();
      maybeAutoKey({ layerId, prop, value, forceKey, autoKey, currentFrame, layerKeyframes, setKeyframe });
    },
    []
  );

  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const displayLayers = React.useMemo(() => buildDisplayOrder(layers), [layers]);

  const handleToggleExpanded = useCallback((id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    }), []);

  const handleRequestDelete = useCallback((id: string) => {
    const { layers: ls, layerThumbnails: thumbs, layerKeyframes: kfs, removeLayer: doRemove } = useAppStore.getState();
    const layer = ls.find((l) => l.id === id);
    if (!layer) return;
    const thumb = thumbs[id];
    const hasPixels = !!thumb && thumb !== getBlankLayerThumbnailDataUrl();
    const layerKfs = kfs[id];
    const hasKeyframes = layerKfs ? Object.values(layerKfs).some((rec) => Object.keys(rec as Record<string, unknown>).length > 0) : false;
    const hasPropertyChanges =
      !layer.visible || !!layer.locked || layer.opacity !== 1 ||
      layer.translate.x !== 0 || layer.translate.y !== 0 ||
      layer.rotate.x !== 0 || layer.rotate.y !== 0 || layer.rotate.z !== 0;
    if (hasPixels || hasKeyframes || hasPropertyChanges) {
      setDeleteConfirmId(id);
    } else {
      viewportActions.pushLayerSnapshot.current();
      handleDelete(id);
    }
  }, []);

  const handleStartEdit = useCallback((id: string, name: string) => {
    setEditingNameId(id);
    setNameDraft(name);
  }, []);

  const handleNameDraftChange = useCallback((name: string) => setNameDraft(name), []);

  const handleNameCommit = useCallback((id: string, name: string) => {
    updateLayer(id, { name });
    setEditingNameId(null);
  }, [updateLayer]);

  type DropState = { mode: 'reorder'; index: number } | { mode: 'parent'; targetId: string } | null;
  const [dropState, setDropState] = useState<DropState>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /** Determines whether a drag position targets a reorder slot or a parent assignment. */
  const computeDropZone = useCallback((clientY: number): DropState => {
    if (!listRef.current) return { mode: 'reorder', index: 0 };
    const rows = Array.from(listRef.current.querySelectorAll<HTMLElement>('[data-layer-row]'));
    for (let i = 0; i < rows.length; i++) {
      const rect = rows[i].getBoundingClientRect();
      if (clientY > rect.bottom) continue;
      const relY = clientY - rect.top;
      const rowH = rect.height;
      if (relY < rowH * 0.25) {
        return { mode: 'reorder', index: i };
      } else if (relY < rowH * 0.75) {
        const layerId = rows[i].getAttribute('data-layer-id');
        if (layerId) return { mode: 'parent', targetId: layerId };
        return { mode: 'reorder', index: i };
      } else {
        return { mode: 'reorder', index: i + 1 };
      }
    }
    return { mode: 'reorder', index: rows.length };
  }, []);

  const handleContainerDragOver = useCallback((evt: React.DragEvent) => {
    evt.preventDefault();
    evt.dataTransfer.dropEffect = 'move';
    setDropState(computeDropZone(evt.clientY));
  }, [computeDropZone]);

  const handleContainerDrop = useCallback((evt: React.DragEvent) => {
    evt.preventDefault();
    const draggedId = evt.dataTransfer.getData('text/plain');
    const ds = dropState;
    setDropState(null);
    if (!draggedId || !ds) return;
    const { layers: ls } = useAppStore.getState();
    const draggedLayer = ls.find((l) => l.id === draggedId);
    if (!draggedLayer) return;

    // Block reorder/reparent when any ancestor of the dragged layer is locked
    const hasLockedAncestor = (layerId: string): boolean => {
      const l = ls.find((x) => x.id === layerId);
      if (!l?.parentId) return false;
      const parent = ls.find((x) => x.id === l.parentId);
      return !!parent && (parent.locked || hasLockedAncestor(parent.id));
    };
    if (hasLockedAncestor(draggedId)) return;

    if (ds.mode === 'parent') {
      // Prevent self-parenting or cycles
      if (ds.targetId === draggedId) return;
      const wouldCycle = (cId: string, pId: string): boolean => {
        if (cId === pId) return true;
        const p = ls.find((l) => l.id === pId);
        return p?.parentId ? wouldCycle(cId, p.parentId) : false;
      };
      if (wouldCycle(draggedId, ds.targetId)) return;
      viewportActions.pushLayerSnapshot.current();
      // Set parentId then reorder so the child lands immediately after its parent's subtree
      const updatedLayers = ls.map((l) => l.id === draggedId ? { ...l, parentId: ds.targetId } : l);
      setLayersOrder(buildDisplayOrder(updatedLayers));
    } else {
      // Reorder mode — work entirely in display space so tree ordering is respected
      const displayLayers = buildDisplayOrder(ls);
      const displayFrom = displayLayers.findIndex((l) => l.id === draggedId);
      const adjustedTo = displayFrom < ds.index ? ds.index - 1 : ds.index;
      if (displayFrom === adjustedTo) return;

      // Validate: child cannot go above its parent
      if (draggedLayer.parentId) {
        const parentDispIdx = displayLayers.findIndex((l) => l.id === draggedLayer.parentId);
        if (adjustedTo <= parentDispIdx) return;
      }

      // Compute desired new display order
      const newDisplay = [...displayLayers];
      const [moved] = newDisplay.splice(displayFrom, 1);
      newDisplay.splice(adjustedTo, 0, moved);

      // Reject positions that break tree structure (e.g. root layer inserted between parent and child)
      const validated = buildDisplayOrder(newDisplay);
      if (validated.map((l) => l.id).join(',') !== newDisplay.map((l) => l.id).join(',')) return;

      viewportActions.pushLayerSnapshot.current();
      setLayersOrder(newDisplay);
    }
  }, [dropState, setLayersOrder]);

  const handleContainerDragLeave = useCallback((evt: React.DragEvent) => {
    if (!listRef.current?.contains(evt.relatedTarget as Node)) {
      setDropState(null);
    }
  }, []);

  const handleAdd = () => {
    const id = `layer-${Date.now()}`;
    viewportActions.pushLayerSnapshot.current();
    addLayer({
      id,
      name: `Layer ${layers.length + 1}`,
      visible: true,
      locked: false,
      opacity: 1,
      translate: { x: 0, y: 0 },
      rotate: { x: 0, y: 0, z: 0 },
    });
    selectLayer(id);
  };

  const handleDelete = useCallback((id: string) => {
    const { layers: ls, addLayer: doAdd, removeLayer: doRemove, selectLayer: doSelect, updateLayer: doUpdate } = useAppStore.getState();
    // Unparent any direct children so they become root layers.
    ls.filter((l) => l.parentId === id).forEach((child) => doUpdate(child.id, { parentId: undefined }));
    const idx = ls.findIndex((l) => l.id === id);
    const remaining = ls.filter((l) => l.id !== id);
    if (remaining.length === 0) {
      // No layers left — create a blank replacement first, then remove.
      const newId = `layer-${Date.now()}`;
      doAdd({
        id: newId,
        name: 'Layer 1',
        visible: true,
        locked: false,
        opacity: 1,
        translate: { x: 0, y: 0 },
        rotate: { x: 0, y: 0, z: 0 },
      });
      doRemove(id);
      doSelect(newId);
    } else {
      // Prefer the layer that will sit below (same index after removal), else the one above.
      const nextIdx = Math.min(idx, remaining.length - 1);
      const nextId = remaining[nextIdx].id;
      doRemove(id);
      doSelect(nextId);
    }
  }, []);

  viewportActions.deleteSelectedLayer.current = () => {
    const id = useAppStore.getState().selectedLayerId;
    if (id) handleRequestDelete(id);
  };

  return (
    <div style={{ padding: 10 }}>
      {/* Delete confirmation modal */}
      <Modal
        open={deleteConfirmId !== null}
        title="Delete layer?"
        onOk={() => {
          if (deleteConfirmId) {
            viewportActions.pushLayerSnapshot.current();
            handleDelete(deleteConfirmId);
          }
          setDeleteConfirmId(null);
        }}
        onCancel={() => setDeleteConfirmId(null)}
        okText="Delete"
        okButtonProps={{ danger: true }}
        width={360}
      >
        <p style={{ margin: 0 }}>
          This layer has painted content or keyframes. Are you sure you want to permanently delete it?
        </p>
      </Modal>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>Layers</h3>
        <div style={{ display: 'flex', gap: 4 }}>
          <LayerToolButton
            icon={<VerticalAlignBottomOutlined />}
            title="Merge selected layer down"
            hoverColor="#f5a623"
            disabled={!selectedLayerId || layers.findIndex((l) => l.id === selectedLayerId) >= layers.length - 1}
            onClick={() => {
              if (!selectedLayerId) return;
              const idx = layers.findIndex((l) => l.id === selectedLayerId);
              const below = layers[idx + 1];
              if (below) requestMergeLayerDown(selectedLayerId, below.id);
            }}
          />
          <LayerToolButton
            icon={<FileAddOutlined />}
            title="Add new layer"
            hoverColor="#52c41a"
            onClick={handleAdd}
          />
          <LayerToolButton
            icon={<ClearOutlined />}
            title="Clear selected layer (Alt+Backspace)"
            hoverColor="#f87171"
            disabled={!selectedLayerId}
            onClick={() => viewportActions.clearLayer.current()}
          />
          <LayerToolButton
            icon={<DeleteOutlined />}
            title="Delete selected layer (Backspace)"
            hoverColor="#ff4d4f"
            disabled={!selectedLayerId}
            onClick={() => { if (selectedLayerId) handleRequestDelete(selectedLayerId); }}
          />
        </div>
      </div>

      <div
        ref={listRef}
        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        onDragOver={handleContainerDragOver}
        onDrop={handleContainerDrop}
        onDragLeave={handleContainerDragLeave}
      >
        <DropIndicator active={dropState?.mode === 'reorder' && dropState.index === 0} />
        {displayLayers.map((layer, layerIndex) => {
          const depth = getLayerDepth(layer.id, layers);
          const ancestorLocked = !layer.locked && isEffectivelyLocked(layer, layers);
          const hasParent = !!layer.parentId && layers.some((l) => l.id === layer.parentId);
          return (
            <React.Fragment key={layer.id}>
              <div data-layer-row data-layer-id={layer.id}>
                <LayerRow
                  layer={layer}
                  isSelected={layer.id === selectedLayerId}
                  isExpanded={expandedIds.has(layer.id)}
                  isEditing={editingNameId === layer.id}
                  nameDraft={editingNameId === layer.id ? nameDraft : ''}
                  thumbnail={layerThumbnails[layer.id]}
                  shiftHeld={shiftHeld}
                  indentLevel={depth}
                  isParentDropTarget={dropState?.mode === 'parent' && dropState.targetId === layer.id}
                  isAncestorLocked={ancestorLocked}
                  onUnparent={hasParent && !ancestorLocked ? () => { viewportActions.pushLayerSnapshot.current(); updateLayer(layer.id, { parentId: undefined }); } : undefined}
                  selectLayer={selectLayer}
                  updateLayer={updateLayer}
                  onToggleExpanded={handleToggleExpanded}
                  onStartEdit={handleStartEdit}
                  onNameDraftChange={handleNameDraftChange}
                  onNameCommit={handleNameCommit}
                  onDragEnd={() => setDropState(null)}
                  callMaybeAutoKey={callMaybeAutoKey}
                />
              </div>
              <DropIndicator active={dropState?.mode === 'reorder' && dropState.index === layerIndex + 1} />
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default LayersPanel;
